import { loopSpeedRamp } from '../progression.js';

/**
 * Anti-cheat run-timing verification — the missing wall-clock proof on
 * top of the deterministic replay validator.
 *
 * The replay validator re-simulates a run from beat-keyed inputs and
 * checks the score reproduces. That proves the inputs are *internally*
 * consistent, but says nothing about whether the run was played in real
 * time: a TAS that slows the whole page down (audio clock + `Date.now`
 * + `performance.now`) lets the player react with superhuman timing yet
 * still emit a beat-perfect replay the validator happily accepts.
 *
 * This module closes part of that gap. The client captures, at each
 * real input event, a triple of `(beat, wallMs, monoMs)` — the audio
 * clock's beat, `Date.now()`, and `performance.now()`. The server then
 * checks two relationships that honest play always satisfies:
 *   1. **beat-vs-wall**: between two inputs Δbeat apart, the real wall
 *      time that passed must match the musical time Δbeat represents at
 *      the loop's playback speed. A run slowed N× shows ~N× the
 *      expected wall gap.
 *   2. **wall-vs-mono**: `Date.now()` and `performance.now()` advance
 *      together (~1:1). An attacker who fakes one clock but not the
 *      other desyncs them.
 *
 * Both checks use the *median* across all input pairs, which is immune
 * to the handful of huge outliers a legitimate pause produces (the
 * audio clock — and therefore sampling — is suspended while paused).
 *
 * An attacker who fakes *all three* clocks in perfect lockstep with the
 * audio defeats both checks here (every ratio stays ~1). The server run
 * token (`src/server/anticheat/runToken.ts`) rejects impossible-fast
 * completions. `runTimingCheck` also compares elapsed with the client's
 * monotonic span to flag possible lockstep slowdowns. That mismatch is
 * diagnostic only: pre-sample pauses and delayed submissions inflate it
 * for honest runs, and forged samples can conceal it.
 *
 * Shared so the server (the authority) and the client (which can warn
 * the player pre-submit, and which owns the capture format) agree on
 * the wire shape and the verdict maths.
 */

/** Wire-format version for {@link RunTimingReport}. Bump on shape changes. */
export const RUN_TIMING_VERSION = 1;

/**
 * One captured `(beat, wallMs, monoMs)` triple, tagged with the loop it
 * belongs to (each infinite-mode loop resets the audio clock, so beats
 * are only comparable within a single loop).
 */
export interface RunTimingSample {
  /** 0-based loop index this sample was captured in. */
  loop: number;
  /** Audio-clock beat at capture time (this loop's 0..totalBeats space). */
  beat: number;
  /** `Date.now()` at capture (real wall clock, ms since epoch). */
  wallMs: number;
  /** `performance.now()` at capture (monotonic, ms since page nav start). */
  monoMs: number;
}

/**
 * The client-captured timing stream for one run, submitted alongside
 * the replay. Deliberately a sibling of the replay (not embedded in it)
 * so the determinism-critical replay format stays untouched and the
 * headless re-simulators never see anti-cheat fields.
 */
export interface RunTimingReport {
  /** {@link RUN_TIMING_VERSION} the client captured against. */
  v: number;
  /** `Date.now()` at the first captured input (run anchor, ms). */
  startWallMs: number;
  /** `performance.now()` at the first captured input (ms). */
  startMonoMs: number;
  /** Total gameplay input events observed (counted before down-sampling). */
  totalInputs: number;
  /** How many of `totalInputs` had `event.isTrusted === false` (synthetic). */
  untrustedInputs: number;
  /**
   * Down-sampled `(beat, wall, mono)` triples, spread uniformly across
   * the run. Capped client-side so a long infinite-mode run can't bloat
   * the payload (see {@link MAX_TIMING_SAMPLES}).
   */
  samples: RunTimingSample[];
}

/**
 * Verdict from {@link verifyRunTiming}. `ok === false` is a hard reject;
 * `flags` are softer signals worth logging even when the run is accepted
 * (so real-world distributions are observable before thresholds tighten).
 */
export interface RunTimingVerdict {
  /** False ⇒ the run should be rejected outright. */
  ok: boolean;
  /** Machine-readable reason codes (rejects and soft flags both land here). */
  flags: RunTimingFlag[];
  /** Median observed/expected wall-time ratio (~1 honest, >1 slow, <1 fast). Null if too few pairs. */
  medianBeatWallRatio: number | null;
  /** Median `Date.now`/`performance.now` delta ratio (~1 honest). Null if too few pairs. */
  medianWallMonoRatio: number | null;
  /** Fraction of inputs flagged synthetic (`untrustedInputs / totalInputs`). */
  untrustedFraction: number;
  /** How many usable consecutive-sample pairs fed the medians. */
  pairCount: number;
}

export type RunTimingFlag =
  | 'insufficient_samples'
  | 'synthetic_input_present'
  | 'synthetic_input_dominant'
  | 'slowdown_suspected'
  | 'slowdown_detected'
  | 'speedup_suspected'
  | 'speedup_detected'
  | 'clock_divergence'
  | 'non_monotonic_clock';

/**
 * Target sample budget per run. A few hundred well-spread points is
 * plenty for a robust median; the client adaptively halves its sampling
 * resolution once the buffer reaches 2× this (so it holds at most
 * ~2× MAX_TIMING_SAMPLES), keeping uniform coverage while bounding the
 * payload (each sample is 4 small numbers).
 */
export const MAX_TIMING_SAMPLES = 1500;

/** Below this many usable pairs we don't have the statistics to judge — never reject. */
const MIN_PAIRS_FOR_VERDICT = 8;
/**
 * Ignore sample pairs closer than this in beats: tiny Δbeat windows
 * (two taps in the same musical instant) divide expected-time down to
 * near-zero and make the ratio numerically unstable.
 */
const MIN_PAIR_BEAT_DELTA = 0.5;

/**
 * Beat-vs-wall ratio bands. Ratio = real wall ms elapsed ÷ musical ms
 * the beat delta represents at the loop's speed. Honest play sits at
 * ~1.0. Bands are deliberately wide — v1 rejects only the egregious
 * (≥1.8× slow / ≤0.55× fast, i.e. well past any device jitter or
 * reaction-time slack) and merely flags the borderline so we can watch
 * the real distribution before tightening.
 */
const SLOWDOWN_REJECT_RATIO = 1.8;
const SLOWDOWN_FLAG_RATIO = 1.35;
const SPEEDUP_REJECT_RATIO = 0.55;
const SPEEDUP_FLAG_RATIO = 0.8;

/**
 * `Date.now`-vs-`performance.now` divergence tolerance. Both are real
 * clocks that track within a millisecond under honest play; browser
 * timer-clamping noise averages out over a whole-run median. A
 * persistent gap beyond this is the signature of one clock being faked
 * but not the other. Flag-only (not reject) in v1 to avoid punishing
 * any exotic-but-legit environment we haven't observed yet.
 */
const WALL_MONO_DIVERGENCE_FLAG = 0.35;

/** Synthetic-input fraction at/above which we reject (a bot driving the game via dispatched events). */
const UNTRUSTED_REJECT_FRACTION = 0.5;

/**
 * The minimum real wall-clock time a *completed* run of `loopCount`
 * loops must have taken, in ms. You cannot finish a song faster than it
 * plays: each loop lasts `totalBeats` beats at its ramped playback speed,
 * so the run's real duration is bounded below by the sum. The server
 * compares this (with generous slack) against the elapsed time its own
 * run token measured, hard-rejecting physically-impossible submissions
 * (canned-replay bots, sped-up clocks) without the pause-ambiguity that
 * plagues the *upper* bound.
 */
export function minRealRunDurationMs(
  loopCount: number,
  totalBeats: number,
  bpm: number,
): number {
  const beatMs = 60_000 / bpm;
  let total = 0;
  for (let loop = 0; loop < loopCount; loop += 1) {
    total += (totalBeats * beatMs) / loopSpeedRamp(loop).startSpeed;
  }
  return total;
}

function median(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Real milliseconds that `beatDelta` beats represent at `bpm`, scaled by
 * the loop's playback speed (higher loops play faster, so the same beat
 * span passes in less wall time).
 */
function expectedWallMsForBeats(beatDelta: number, bpm: number, speed: number): number {
  return (beatDelta * 60_000) / (bpm * speed);
}

/**
 * Verify a captured {@link RunTimingReport} against the chart it claims
 * to be a run of. Pure and deterministic — the server calls it as the
 * authority, but it could equally drive a client-side pre-submit warning.
 *
 * Returns a {@link RunTimingVerdict}; the caller decides policy (reject
 * on `!ok`, log every `flag`). `bpm` is the chart tempo; per-loop speed
 * is derived from {@link loopSpeedRamp} so the maths matches exactly what
 * the audio engine scheduled.
 */
export function verifyRunTiming(
  report: RunTimingReport,
  opts: { bpm: number },
): RunTimingVerdict {
  const { bpm } = opts;
  const flags: RunTimingFlag[] = [];
  const totalInputs = Math.max(0, report.totalInputs);
  const untrustedFraction = totalInputs > 0 ? report.untrustedInputs / totalInputs : 0;

  let ok = true;

  // --- Synthetic-input (event.isTrusted) check -------------------------
  if (untrustedFraction >= UNTRUSTED_REJECT_FRACTION) {
    ok = false;
    flags.push('synthetic_input_dominant');
  } else if (report.untrustedInputs > 0) {
    flags.push('synthetic_input_present');
  }

  // --- Build per-pair ratios within each loop --------------------------
  const beatWallRatios: number[] = [];
  const wallMonoRatios: number[] = [];
  let nonMonotonic = false;
  const samples = report.samples;
  let prev = samples[0];
  for (let i = 1; i < samples.length; i += 1) {
    const cur = samples[i];
    if (cur.loop !== prev.loop) {
      prev = cur;
      continue;
    }
    const beatDelta = cur.beat - prev.beat;
    const wallDelta = cur.wallMs - prev.wallMs;
    const monoDelta = cur.monoMs - prev.monoMs;
    // Real clocks never run backwards within a loop. A negative delta is
    // either a tampered timestamp or a malformed payload — note it and
    // skip the pair so it can't poison the medians.
    const adjacent = samples[i - 1];
    if (cur.wallMs < adjacent.wallMs || cur.monoMs < adjacent.monoMs || cur.beat < adjacent.beat) {
      nonMonotonic = true;
      prev = cur;
      continue;
    }
    // Keep the anchor until enough beats pass; normal 150ms heartbeats
    // and dense input events are individually closer than half a beat.
    if (beatDelta < MIN_PAIR_BEAT_DELTA) {
      continue;
    }
    const speed = loopSpeedRamp(cur.loop).startSpeed;
    const expectedMs = expectedWallMsForBeats(beatDelta, bpm, speed);
    beatWallRatios.push(wallDelta / expectedMs);
    if (monoDelta > 0) {
      wallMonoRatios.push(wallDelta / monoDelta);
    }
    prev = cur;
  }

  if (nonMonotonic) {
    flags.push('non_monotonic_clock');
  }

  const medianBeatWallRatio = median(beatWallRatios);
  const medianWallMonoRatio = median(wallMonoRatios);
  const pairCount = beatWallRatios.length;

  if (pairCount < MIN_PAIRS_FOR_VERDICT || medianBeatWallRatio === null) {
    // Too little signal to judge rate honestly. Keep any synthetic-input
    // verdict already decided above, but don't add a rate reject.
    flags.push('insufficient_samples');
    return {
      ok,
      flags,
      medianBeatWallRatio,
      medianWallMonoRatio,
      untrustedFraction,
      pairCount,
    };
  }

  // --- beat-vs-wall (slowdown / speedup) -------------------------------
  if (medianBeatWallRatio >= SLOWDOWN_REJECT_RATIO) {
    ok = false;
    flags.push('slowdown_detected');
  } else if (medianBeatWallRatio >= SLOWDOWN_FLAG_RATIO) {
    flags.push('slowdown_suspected');
  } else if (medianBeatWallRatio <= SPEEDUP_REJECT_RATIO) {
    ok = false;
    flags.push('speedup_detected');
  } else if (medianBeatWallRatio <= SPEEDUP_FLAG_RATIO) {
    flags.push('speedup_suspected');
  }

  // --- wall-vs-mono (single-clock tampering) ---------------------------
  if (
    medianWallMonoRatio !== null &&
    Math.abs(medianWallMonoRatio - 1) > WALL_MONO_DIVERGENCE_FLAG
  ) {
    flags.push('clock_divergence');
  }

  return {
    ok,
    flags,
    medianBeatWallRatio,
    medianWallMonoRatio,
    untrustedFraction,
    pairCount,
  };
}
